import { useState } from "react";
import api from "@/lib/api";
import Visualizer from "@/components/Visualizer";
import Keyboard from "@/components/Keyboard";
import Knob from "@/components/Knob";
import { playNote, play808 } from "@/lib/audio";
import { useAuth } from "@/context/AuthContext";
import { Lightning, Sparkle, FloppyDisk, Play, WaveSawtooth, WaveSine, WaveSquare, WaveTriangle } from "@phosphor-icons/react";

const DEFAULT_PRESET = {
  name: "STARTER_VOID_001",
  prompt: "starter",
  tags: ["trap"],
  waveform: "sawtooth",
  sub_octave: -1,
  glide_ms: 80,
  drive: 0.55,
  saturation: 0.4,
  bitcrush: 0.1,
  filter_cutoff: 0.55,
  filter_resonance: 0.35,
  attack: 0.02,
  decay: 0.2,
  sustain: 0.6,
  release: 0.25,
  reverb: 0.35,
  delay: 0.18,
  chorus: 0.15,
  stereo_width: 0.7,
  pitch_semitones: 0,
  detune_cents: 6,
  lfo_rate: 2.5,
  lfo_depth: 0.15,
  color_primary: "#FF003C",
  color_secondary: "#8B00FF",
};

const WAVE_OPTIONS = [
  { v: "sine", Icon: WaveSine, label: "SINE" },
  { v: "sawtooth", Icon: WaveSawtooth, label: "SAW" },
  { v: "square", Icon: WaveSquare, label: "SQR" },
  { v: "triangle", Icon: WaveTriangle, label: "TRI" },
];

export default function Playground() {
  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [prompt, setPrompt] = useState("dark rage lead");
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const { user } = useAuth();

  // 808 state
  const [drum, setDrum] = useState({ pitch: 36, drive: 0.55, glide: 0.15, punch: 0.65, sub: 0.85, saturation: 0.45, duration: 0.7 });

  const update = (k, v) => setPreset((p) => ({ ...p, [k]: v }));

  const generate = async () => {
    setBusy(true);
    try {
      const endpoint = user ? "/ai/generate-preset" : "/ai/generate-preset-public";
      const { data } = await api.post(endpoint, { prompt });
      setPreset(data.preset);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const savePreset = async () => {
    if (!user) { setSaveMsg("Log in to save"); setTimeout(() => setSaveMsg(""), 2000); return; }
    try {
      await api.post("/presets", { preset });
      setSaveMsg("SAVED ✓");
    } catch (e) {
      setSaveMsg("FAILED");
    }
    setTimeout(() => setSaveMsg(""), 2000);
  };

  const audition = () => {
    [60, 63, 67, 70].forEach((n, i) => setTimeout(() => playNote(preset, n, 0.4), i * 180));
  };

  return (
    <div className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10" data-testid="playground-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-2">◢ PLAYGROUND</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-6xl leading-[0.9]">
            THE <span className="text-brand-red neon-red">SYNTH</span> LAB.
          </h1>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">
          MODE: <span className="text-brand-red">LIVE</span> · LATENCY: 0.04s · CPU: 12%
        </div>
      </div>

      {/* AI prompt strip */}
      <div className="border border-zinc-800 bg-bg-surface p-5 grain mb-6" data-testid="ai-prompt-bar">
        <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-ink-dim mb-3">
          <Sparkle size={14} weight="fill" className="text-brand-red" /> AI PROMPT → PRESET
        </div>
        <div className="flex flex-wrap gap-3 items-stretch">
          <div className="flex border border-zinc-800 focus-within:border-brand-red flex-1 min-w-[260px]">
            <span className="px-3 text-brand-red font-bold flex items-center">›</span>
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              data-testid="playground-prompt-input"
              placeholder="dark rage lead / detroit bell / space pad …"
              className="flex-1 bg-transparent py-3 text-sm font-mono outline-none"
            />
            <button onClick={generate} disabled={busy} data-testid="playground-generate-btn" className="bg-brand-red text-black text-[10px] tracking-[0.3em] uppercase font-bold px-4 hover:bg-white disabled:opacity-50">
              {busy ? "..." : "GEN ▸"}
            </button>
          </div>
          <button onClick={audition} data-testid="playground-audition-btn" className="border border-zinc-700 text-white text-[10px] tracking-[0.3em] uppercase font-bold px-4 py-3 hover:border-brand-purple hover:text-brand-purple inline-flex items-center gap-2">
            <Play size={12} weight="fill" /> AUDITION
          </button>
          <button onClick={savePreset} data-testid="playground-save-btn" className="border border-zinc-700 text-white text-[10px] tracking-[0.3em] uppercase font-bold px-4 py-3 hover:border-brand-red hover:text-brand-red inline-flex items-center gap-2">
            <FloppyDisk size={12} weight="fill" /> {saveMsg || "SAVE"}
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left: synth controls */}
        <div className="lg:col-span-8 border border-zinc-800 bg-bg-surface p-6 grain">
          <div className="flex items-baseline justify-between mb-4">
            <div className="font-display font-black tracking-tighter text-2xl uppercase">
              {preset.name}
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">{preset.waveform}</div>
          </div>

          <Visualizer height={140} style="wave" color={preset.color_primary} color2={preset.color_secondary} />

          {/* Waveform select */}
          <div className="mt-6">
            <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mb-2">OSCILLATOR / WAVE</div>
            <div className="grid grid-cols-4 gap-2">
              {WAVE_OPTIONS.map(({ v, Icon, label }) => (
                <button
                  key={v}
                  onClick={() => update("waveform", v)}
                  data-testid={`wave-${v}`}
                  className={`border p-3 flex flex-col items-center gap-1.5 transition-colors ${
                    preset.waveform === v ? "border-brand-red bg-brand-red/10 text-white" : "border-zinc-800 text-ink-mute hover:border-zinc-600 hover:text-white"
                  }`}
                >
                  <Icon size={20} weight="bold" />
                  <span className="text-[10px] tracking-[0.2em] uppercase">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Knobs */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Knob label="DRIVE" value={preset.drive} onChange={(v) => update("drive", v)} testid="knob-drive" />
            <Knob label="SATURATE" value={preset.saturation} onChange={(v) => update("saturation", v)} testid="knob-saturation" />
            <Knob label="BITCRUSH" value={preset.bitcrush} onChange={(v) => update("bitcrush", v)} testid="knob-bitcrush" />
            <Knob label="WIDTH" value={preset.stereo_width} onChange={(v) => update("stereo_width", v)} testid="knob-width" />
            <Knob label="CUTOFF" value={preset.filter_cutoff} onChange={(v) => update("filter_cutoff", v)} testid="knob-cutoff" />
            <Knob label="RESONANCE" value={preset.filter_resonance} onChange={(v) => update("filter_resonance", v)} testid="knob-resonance" />
            <Knob label="REVERB" value={preset.reverb} onChange={(v) => update("reverb", v)} testid="knob-reverb" />
            <Knob label="DELAY" value={preset.delay} onChange={(v) => update("delay", v)} testid="knob-delay" />
            <Knob label="ATTACK" value={preset.attack} onChange={(v) => update("attack", v)} testid="knob-attack" />
            <Knob label="DECAY" value={preset.decay} onChange={(v) => update("decay", v)} testid="knob-decay" />
            <Knob label="SUSTAIN" value={preset.sustain} onChange={(v) => update("sustain", v)} testid="knob-sustain" />
            <Knob label="RELEASE" value={preset.release} onChange={(v) => update("release", v)} testid="knob-release" />
            <Knob label="LFO RATE" value={preset.lfo_rate} min={0.1} max={10} step={0.1} unit="Hz" onChange={(v) => update("lfo_rate", v)} testid="knob-lfo-rate" />
            <Knob label="LFO DEPTH" value={preset.lfo_depth} onChange={(v) => update("lfo_depth", v)} testid="knob-lfo-depth" />
            <Knob label="DETUNE" value={preset.detune_cents} min={0} max={50} step={0.5} unit="¢" onChange={(v) => update("detune_cents", v)} testid="knob-detune" />
            <Knob label="PITCH" value={preset.pitch_semitones} min={-24} max={24} step={1} unit="st" onChange={(v) => update("pitch_semitones", v)} testid="knob-pitch" />
          </div>

          {/* Keyboard */}
          <div className="mt-6 border border-zinc-900 p-4 bg-black/60" id="designer">
            <div className="flex items-center justify-between mb-3 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
              <span>KEYBOARD — 2 OCTAVES</span>
              <span>CLICK TO TRIGGER ⌖</span>
            </div>
            <div className="overflow-x-auto">
              <Keyboard preset={preset} />
            </div>
          </div>
        </div>

        {/* Right: 808 designer */}
        <div className="lg:col-span-4 border border-zinc-800 bg-bg-surface p-6 grain self-start">
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={16} weight="fill" className="text-brand-red" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-ink-dim">808 DESIGNER</span>
          </div>
          <Visualizer height={120} style="bars" />

          <div className="grid grid-cols-2 gap-4 mt-5">
            <Knob label="PITCH" value={drum.pitch} min={20} max={50} step={1} unit="" onChange={(v) => setDrum({ ...drum, pitch: v })} testid="808-pitch" />
            <Knob label="DURATION" value={drum.duration} min={0.2} max={2} step={0.05} unit="s" onChange={(v) => setDrum({ ...drum, duration: v })} testid="808-duration" />
            <Knob label="DRIVE" value={drum.drive} onChange={(v) => setDrum({ ...drum, drive: v })} testid="808-drive" />
            <Knob label="GLIDE" value={drum.glide} min={0} max={0.5} step={0.01} onChange={(v) => setDrum({ ...drum, glide: v })} testid="808-glide" />
            <Knob label="PUNCH" value={drum.punch} onChange={(v) => setDrum({ ...drum, punch: v })} testid="808-punch" />
            <Knob label="SUB" value={drum.sub} onChange={(v) => setDrum({ ...drum, sub: v })} testid="808-sub" />
            <Knob label="SATURATION" value={drum.saturation} onChange={(v) => setDrum({ ...drum, saturation: v })} testid="808-saturation" />
          </div>

          <button
            onClick={() => play808(drum)}
            data-testid="808-trigger-btn"
            className="mt-6 w-full bg-brand-red text-black text-xs font-bold tracking-[0.25em] uppercase py-4 hover:bg-white transition-colors btn-glitch"
          >
            ▸ TRIGGER 808
          </button>

          <div className="mt-5 grid grid-cols-4 gap-1.5">
            {[30, 33, 36, 38, 40, 43, 45, 48].map((p) => (
              <button
                key={p}
                onClick={() => play808({ ...drum, pitch: p })}
                data-testid={`808-pad-${p}`}
                className="border border-zinc-800 hover:border-brand-red hover:bg-brand-red/10 py-3 text-[10px] tracking-[0.2em] uppercase text-ink-mute hover:text-white transition-colors"
              >
                {p}
              </button>
            ))}
          </div>

          {/* Tags & info */}
          <div className="mt-6 pt-5 border-t border-zinc-900">
            <div className="text-[9px] tracking-[0.3em] uppercase text-ink-dim mb-2">PRESET TAGS</div>
            <div className="flex flex-wrap gap-1.5">
              {preset.tags?.map((t) => (
                <span key={t} className="text-[9px] tracking-[0.2em] uppercase border border-zinc-800 px-1.5 py-0.5 text-ink-mute">#{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
